import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Presentation, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useApp } from '../context/AppContext';
import { saveScenario } from '../lib/api';
import { toast } from 'sonner';
import logger from '../lib/logger';

/**
 * BoardReadyButton - Button that navigates to Board Ready view
 * If calculation isn't saved, prompts user to save first
 * 
 * @param {string} analysisType - 'single' | 'portfolio' | 'matrix'
 * @param {string} defaultName - Default name for the scenario
 * @param {string} className - Additional CSS classes
 */
const BoardReadyButton = ({ 
  analysisType = 'single',
  defaultName = 'My Analysis',
  className = ''
}) => {
  const navigate = useNavigate();
  const { 
    currentScenario, 
    setCurrentScenario, 
    configuration, 
    results, 
    narrative 
  } = useApp();
  
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scenarioName, setScenarioName] = useState(defaultName);
  const [scenarioDescription, setScenarioDescription] = useState('');

  const handleClick = () => {
    if (currentScenario?.id) {
      // Already saved, navigate directly
      navigate(`/board-ready/${currentScenario.id}`);
    } else {
      // Not saved, show save dialog
      setShowSaveDialog(true);
    }
  };

  const handleSaveAndNavigate = async () => {
    if (!scenarioName.trim()) {
      toast.error('Please enter a scenario name');
      return;
    }

    setIsSaving(true);
    try {
      const scenarioData = {
        name: scenarioName.trim(),
        description: scenarioDescription.trim(),
        configuration,
        results,
        narrative: narrative || null,
        analysis_type: analysisType,
      };

      const response = await saveScenario(scenarioData);
      
      // Update current scenario in context
      setCurrentScenario({
        id: response.data.id,
        name: response.data.name,
        share_token: response.data.share_token,
      });

      toast.success('Scenario saved!');
      setShowSaveDialog(false);
      
      // Navigate to Board Ready view
      navigate(`/board-ready/${response.data.id}`);
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        className={`bg-purple-600 hover:bg-purple-700 text-white ${className}`}
        data-testid="board-ready-btn"
      >
        <Presentation className="mr-2 h-4 w-4" />
        Board Ready View
      </Button>

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-purple-600" />
              Save Before Viewing Board Ready
            </DialogTitle>
            <DialogDescription>
              To generate a Board Ready view, your calculation needs to be saved first. 
              Give it a name and we'll take you there.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="scenario-name">Scenario Name *</Label>
              <Input
                id="scenario-name"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g., Q1 Training ROI Analysis"
                data-testid="board-ready-save-name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="scenario-description">Description (optional)</Label>
              <Textarea
                id="scenario-description"
                value={scenarioDescription}
                onChange={(e) => setScenarioDescription(e.target.value)}
                placeholder="Brief description of this analysis..."
                rows={2}
                data-testid="board-ready-save-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSaveDialog(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAndNavigate}
              disabled={isSaving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="board-ready-save-continue"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Presentation className="mr-2 h-4 w-4" />
                  Save & View Board Ready
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BoardReadyButton;
